import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CMS_LEGAL_LINKS } from "../../constants/cms";
import { fetchPublicTerms, acceptTerms, cmsExtractMessage } from "../../services/cms.service";
import { termsContentToDisplayHtml } from "../../utils/termsModuleCms";
import "../../assets/styles/cms_terms_acceptance.scss";

export default function ModuleTermsAcceptanceModal({
  open,
  moduleName,
  moduleLabel,
  onAccepted,
  onClose,
}) {
  const [agreed, setAgreed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [termsContent, setTermsContent] = useState("");
  const [termsVersion, setTermsVersion] = useState(null);
  const [drawerUrl, setDrawerUrl] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !moduleName) return;
    setAgreed(true);
    setError("");
    setDrawerUrl(null);
    let cancelled = false;
    (async () => {
      try {
        const terms = await fetchPublicTerms(moduleName);
        if (!cancelled) {
          setTermsContent(
            termsContentToDisplayHtml(terms?.content, {
              moduleName,
              moduleLabel,
            }),
          );
          setTermsVersion(terms?.version ?? null);
        }
      } catch (err) {
        if (!cancelled) setError(cmsExtractMessage(err, "Failed to load terms"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, moduleName]);

  const handleContinue = async () => {
    if (!agreed || !moduleName) return;
    setLoading(true);
    setError("");
    try {
      await acceptTerms(moduleName);
      onAccepted?.();
    } catch (err) {
      setError(cmsExtractMessage(err, "Failed to record acceptance"));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="terms-acceptance-modal" role="dialog" aria-modal="true" aria-labelledby="terms-acceptance-title">
      <div className="terms-acceptance-modal__backdrop" aria-hidden />
      <div className="terms-acceptance-modal__panel">
        <div className="terms-acceptance-modal__header">
          <h2 id="terms-acceptance-title">Terms &amp; Conditions — {moduleLabel}</h2>
        </div>

        <div className="terms-acceptance-modal__links">
          {CMS_LEGAL_LINKS.map((link) => (
            <button
              key={link.to}
              type="button"
              className="terms-acceptance-modal__link-btn"
              onClick={() => setDrawerUrl(link.to)}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="terms-acceptance-modal__content-layout">
          <div
            className="terms-acceptance-modal__terms cms-rendered-html"
            dangerouslySetInnerHTML={{ __html: termsContent }}
          />
          {drawerUrl ? (
            <aside className="terms-acceptance-modal__drawer" aria-label="Policy document">
              <div className="terms-acceptance-modal__drawer-header">
                <a href={drawerUrl} target="_blank" rel="noopener noreferrer">
                  Open in new tab
                </a>
                <button type="button" onClick={() => setDrawerUrl(null)} aria-label="Close drawer">
                  ×
                </button>
              </div>
              <iframe title="Policy document" src={drawerUrl} className="terms-acceptance-modal__drawer-frame" />
            </aside>
          ) : null}
        </div>

        {termsVersion ? (
          <p className="terms-acceptance-modal__version">Version {termsVersion}</p>
        ) : null}

        <label className="terms-acceptance-modal__checkbox">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span>I have read and agree to the Terms &amp; Conditions.</span>
        </label>

        {error ? <p className="terms-acceptance-modal__error">{error}</p> : null}

        <div className="terms-acceptance-modal__actions">
          <button
            type="button"
            className="sa-btn sa-btn--primary"
            disabled={!agreed || loading}
            onClick={handleContinue}
          >
            {loading ? "Saving..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
